import type { DisplayJob } from '../../types';
import { coerceToStringArray } from './coerce-to-string-array';

function norm(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9åäöüÅÄÖÜ]+/)
    .filter((t) => t.length >= 2)
    .slice(0, 32);
}

function haystackIncludesValue(hay: string, value: string): boolean {
  const v = norm(value);
  if (!v) return false;
  if (hay.includes(v)) return true;
  return tokenize(v).some((t) => t.length > 2 && hay.includes(t));
}

function joinJobList(values: string[] | undefined): string {
  return coerceToStringArray(values)
    .map((x) => norm(String(x)))
    .filter(Boolean)
    .join(' ');
}

/**
 * Builds a lowercase haystack from {@link DisplayJob} fields relevant to a facet
 * attribute name (Algolia / URL refinement keys).
 */
export function jobHaystackForFacetAttribute(attribute: string, job: DisplayJob): string {
  const a = String(attribute).replace(/_/g, '').toLowerCase();

  const parts: string[] = [];

  const push = (...chunks: Array<string | undefined>) => {
    for (const c of chunks) {
      if (c != null && String(c).trim()) parts.push(norm(String(c)));
    }
  };

  if (a === 'requiredlanguages' || a === 'optionallanguages') {
    push(joinJobList(job.requiredLanguages), joinJobList(job.optionalLanguages));
  } else if (a === 'benefits') {
    push(joinJobList(job.benefits), joinJobList(job.perks));
  } else if (a === 'skillsandtechnologies' || a === 'specialisations') {
    push(joinJobList(job.skillsAndTechnologies), joinJobList(job.specialisations));
  } else if (a === 'educationlevel') {
    push(job.educationLevel);
  } else if (a === 'peoplemanagementtier') {
    push(job.peopleManagementTier);
  } else if (a === 'issummerjob' || a === 'summerjob') {
    push(job.jobType, job.title, job.normalisedTitle, job.field);
  } else if (a === 'visasponsor') {
    push(String(job.visaSponsor === true ? 'visa sponsor sponsorship' : ''));
  } else {
    // Unknown facet: broad job text (cheap substring match)
    push(
      job.title,
      job.normalisedTitle,
      job.field,
      job.description,
      job.metaDescription,
      joinJobList(job.skillsAndTechnologies),
      joinJobList(job.requirements),
      joinJobList(job.responsibilities),
      job.companyIndustry,
      job.companyId,
      job.jobType
    );
  }

  return parts.filter(Boolean).join(' ');
}

/**
 * 0–100 score for one refinement value against the job for the given facet attribute.
 */
export function scoreJobAgainstFacetRefinement(
  job: DisplayJob,
  attribute: string,
  value: string
): number {
  const v = String(value).trim();
  if (!v) return 50;

  const a = String(attribute).replace(/_/g, '').toLowerCase();

  if (a === 'issummerjob' || a === 'summerjob') {
    const hay = `${job.title ?? ''} ${job.normalisedTitle ?? ''} ${job.jobType ?? ''} ${job.field ?? ''}`.toLowerCase();
    const summer =
      hay.includes('summer') ||
      hay.includes('kesä') ||
      hay.includes('kesa') ||
      hay.includes('sommar');
    const wantsSummer = /^(1|true|yes)$/i.test(v) || /summer|kesä|kesa|sommar/i.test(v);
    if (wantsSummer) return summer ? 100 : 25;
    return 50;
  }

  if (a === 'visasponsor') {
    const wants = /true|yes|1|visa/i.test(v);
    if (!wants) return 50;
    return job.visaSponsor === true ? 100 : 20;
  }

  const hay = jobHaystackForFacetAttribute(attribute, job);
  if (!hay) return 40;
  return haystackIncludesValue(hay, v) ? 100 : 18;
}

/**
 * Weighted average of scores for facet values (weights from conflict-aware merge).
 */
export function scoreJobAgainstWeightedFacetValues(
  job: DisplayJob,
  attribute: string,
  weights: Map<string, number>
): number {
  if (weights.size === 0) return 50;
  let sum = 0;
  let wsum = 0;
  for (const [val, w] of weights) {
    if (w <= 0) continue;
    wsum += w;
    sum += scoreJobAgainstFacetRefinement(job, attribute, val) * w;
  }
  if (wsum <= 0) return 50;
  return Math.max(0, Math.min(100, Math.round(sum / wsum)));
}
